/**
 * Assisted Journey utility functions
 * Used for Share Consent Link feature - QR code, WhatsApp, SMS sharing
 */

import type { ProductRecord } from '../services/products-api.service';

export interface AssistedJourneyFlags {
  enabled: boolean;
  smsEnabled: boolean;
  whatsappEnabled: boolean;
}

/**
 * Check if assisted journey is globally enabled (master switch).
 * This is set once during app initialization by auth-init-guard.service.ts.
 */
export const isAssistedJourneyGloballyEnabled = (): boolean => {
  return sessionStorage.getItem('assistedJourneyEnabled') === 'true';
};

/**
 * Reads assisted journey feature flags from sessionStorage.
 * These flags are set once during app initialization by auth-init-guard.service.ts
 * and remain constant for the session lifetime.
 *
 * @deprecated Use getFinalAssistedJourneyFlags() for product-level configuration
 */
export const getAssistedJourneyFlags = (): AssistedJourneyFlags => ({
  enabled: sessionStorage.getItem('assistedJourneyEnabled') === 'true',
  smsEnabled: sessionStorage.getItem('assistedJourneySmsEnabled') === 'true',
  whatsappEnabled: sessionStorage.getItem('assistedJourneyWhatsappEnabled') === 'true',
});

/**
 * Get assisted journey flags for a single product from its feature_config.
 * @param product - The product record with feature_config
 * @returns Assisted journey flags for the product
 */
export const getProductAssistedJourneyFlags = (product: ProductRecord): AssistedJourneyFlags => {
  const config = product.feature_config?.assisted_journey;
  return {
    enabled: config?.enabled === true,
    smsEnabled: config?.sms === true,
    whatsappEnabled: config?.whatsapp === true,
  };
};

/**
 * Compute assisted journey flags for multiple products using OR logic.
 * A flag is true if AT LEAST ONE selected product has it enabled.
 *
 * AIDEV-NOTE: FP-6598 — multi-product consents use OR (per requirement: "if at least
 * one product has the SMS feature enabled, send the SMS"). This governs UI visibility
 * only; FinProServer independently re-resolves the FIU/product/journey hierarchy and
 * channel before any SMS is actually sent.
 *
 * @param selectedProductCodes - Array of selected product codes
 * @param productsList - Array of all products with their feature_config
 * @returns Combined assisted journey flags (OR logic across all selected products)
 */
export const computeAssistedJourneyFlags = (
  selectedProductCodes: string[],
  productsList: ProductRecord[]
): AssistedJourneyFlags => {
  // If no products selected, return all false
  if (selectedProductCodes.length === 0) {
    return { enabled: false, smsEnabled: false, whatsappEnabled: false };
  }

  // Filter to get selected products
  const selectedProducts = productsList.filter(p =>
    selectedProductCodes.includes(p.product_code)
  );

  // If no matching products found, return all false
  if (selectedProducts.length === 0) {
    return { enabled: false, smsEnabled: false, whatsappEnabled: false };
  }

  // OR logic: any selected product with the flag enabled turns it on
  return {
    enabled: selectedProducts.some(p =>
      p.feature_config?.assisted_journey?.enabled === true
    ),
    smsEnabled: selectedProducts.some(p =>
      p.feature_config?.assisted_journey?.sms === true
    ),
    whatsappEnabled: selectedProducts.some(p =>
      p.feature_config?.assisted_journey?.whatsapp === true
    ),
  };
};

/**
 * Get final assisted journey flags considering both global and product level.
 *
 * Logic:
 * 1. Check global flag (master switch) from sessionStorage
 * 2. If globally disabled, return all false
 * 3. If globally enabled, compute product-level flags using AND logic
 *
 * @param selectedProductCodes - Array of selected product codes
 * @param productsList - Array of all products with their feature_config
 * @returns Final assisted journey flags
 */
export const getFinalAssistedJourneyFlags = (
  selectedProductCodes: string[],
  productsList: ProductRecord[]
): AssistedJourneyFlags => {
  // Step 1: Check global flag (master switch)
  if (!isAssistedJourneyGloballyEnabled()) {
    return { enabled: false, smsEnabled: false, whatsappEnabled: false };
  }

  // Step 2: Compute product-level flags
  return computeAssistedJourneyFlags(selectedProductCodes, productsList);
};

/**
 * Normalizes a phone number for WhatsApp API.
 * Adds Indian country code (91) if the number is 10 digits.
 * @param mobile - The mobile number string
 * @returns Normalized phone number with country code
 */
export const toWhatsAppNumber = (mobile: string): string => {
  const digits = mobile.replace(/\D/g, '');
  return digits.length === 10 ? `91${digits}` : digits;
};
